import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input, Layout, Menu, Typography, Form, Switch } from 'antd';
import { PlusOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { USER_ROLES } from 'utils/constant';
import Select from 'components/Atom/Select';
import {
  Library,
  properties,
  operators,
  propertiesHash,
  relative_attributes,
  transaction_attributes,
  options,
  frequency,
  stringOnlyFields,
  commonProperties,
  comparisonAllowedProps,
} from '../assets/services';
import { useSelector } from 'react-redux';
import { updateRule, createRule } from 'api/xShields';
import { checkUserPermissions } from 'utils/services';
import { Attributes, OperatorsIcon, VelocityIcon, PredefinedIcon } from 'assets/icons';
import { ruleValidation } from './validation';
const { Sider, Content } = Layout;
const { Title, Paragraph } = Typography;

export default function CreateFraudRuleModal({ isOpen, isEdit, setIsEdit, ruleData, refetch, onClose }) {
  const [ruleName, setRuleName] = useState('');
  const [selectedKey, setSelectedKey] = useState('1');
  const [showDescription, setShowDescription] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [velocityType, setVelocityType] = useState('transaction');
  const [rawText, setRawText] = useState('');
  const [isRuleAdding, setIsRuleAdding] = useState(false);
  const store = useSelector((state) => state?.company?.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  const [isEnabled, setIsEnabled] = useState(true);
  const [validationErrors, setValidationErrors] = useState({
    ruleName: '',
    ruleExpression: '',
  });
  const [validationSuccess, setValidationSuccess] = useState(false);
  const divRef = useRef(null);
  const caretPostionRangeRef = useRef(0);

  useEffect(() => {
    if (isOpen && isEdit && ruleData) {
      setRuleName(ruleData?.name || '');
      setRawText(ruleData?.rule || '');
      setIsEnabled(ruleData?.enabled);
      if (divRef?.current) {
        divRef.current.innerHTML = ruleData?.rule;
        applyHighlightingAndPreserveCursor(ruleData?.rule);
      }
    }
  }, [isOpen, isEdit, ruleData, divRef?.current]);

  const setInitialCaretPositionRange = () => {
    if (!divRef?.current) return;
    const range = document?.createRange();
    range.setStart(divRef?.current, 0);
    range.collapse(true);
    caretPostionRangeRef.current = range?.cloneRange();
  };

  const caretPostion = () => {
    const div = divRef?.current;
    if (!div) return;

    const selection = window?.getSelection();
    if (selection && selection?.rangeCount > 0) {
      caretPostionRangeRef.current = selection?.getRangeAt(0)?.cloneRange();
    }
  };

  const applyHighlightingAndPreserveCursor = (text) => {
    const div = divRef?.current;
    if (!div) return;
    const sel = window?.getSelection();
    let savedRange = null;
    let savedOffset = 0;
    let textNodeIndex = 0;
    let absolutePosition = 0;

    if (sel && sel?.rangeCount > 0) {
      savedRange = sel?.getRangeAt(0)?.cloneRange();
      savedOffset = savedRange?.startOffset;
      let node = savedRange?.startContainer;

      if (div.contains(node)) {
        let walker = document?.createTreeWalker(div, NodeFilter?.SHOW_TEXT);
        let currentNode;
        let index = 0;

        while ((currentNode = walker?.nextNode())) {
          if (currentNode === node) {
            absolutePosition += savedOffset;
            textNodeIndex = index;
            break;
          }
          absolutePosition += currentNode?.textContent?.length || 0;
          index++;
        }
      }
    }
    const originalText = text;
    const coloredHtml = parseTextToHtml(text);
    div.innerHTML = coloredHtml;
    try {
      if (sel) {
        sel?.removeAllRanges();
        let currentPosition = 0;
        let targetNode = null;
        let targetOffset = 0;
        const tokenRegex = /:[a-zA-Z0-9_]+:/g;
        let tokenMatch;
        let tokenPositions = [];

        while ((tokenMatch = tokenRegex?.exec(originalText)) !== null) {
          const tokenEnd = tokenMatch?.index + tokenMatch[0]?.length;
          tokenPositions?.push(tokenEnd);
        }
        const isAfterToken = tokenPositions?.includes(absolutePosition);
        const treeWalker = document?.createTreeWalker(div, NodeFilter?.SHOW_TEXT);
        let currentNode;

        while ((currentNode = treeWalker?.nextNode())) {
          const nodeLength = currentNode?.textContent?.length || 0;
          if (currentPosition <= absolutePosition && absolutePosition <= currentPosition + nodeLength) {
            targetNode = currentNode;
            targetOffset = absolutePosition - currentPosition;
            break;
          }

          currentPosition += nodeLength;
        }
        if (targetNode) {
          const newRange = document.createRange();
          newRange?.setStart(targetNode, Math?.min(targetOffset, targetNode?.textContent?.length || 0));
          newRange?.collapse(true);
          sel?.addRange(newRange);
          caretPostionRangeRef.current = newRange?.cloneRange();
        } else if (isAfterToken) {
          const matchingPosition = tokenPositions?.find((pos) => pos === absolutePosition);

          if (matchingPosition) {
            const allNodes = Array.from(div?.querySelectorAll('*'));
            let found = false;

            for (let i = 0; i < allNodes?.length; i++) {
              const node = allNodes[i];
              if (node?.nodeType === Node?.ELEMENT_NODE && node?.textContent?.match(tokenRegex) && node?.textContent?.length + currentPosition >= matchingPosition) {
                if (node?.nextSibling && node?.nextSibling?.nodeType === Node?.TEXT_NODE) {
                  const newRange = document?.createRange();
                  newRange?.setStart(node?.nextSibling, 0);
                  newRange?.collapse(true);
                  sel?.addRange(newRange);
                  caretPostionRangeRef.current = newRange?.cloneRange();
                  found = true;
                  break;
                } else if (!node.nextSibling) {
                  const textNode = document?.createTextNode('');
                  node?.parentNode?.insertBefore(textNode, node?.nextSibling);
                  const newRange = document?.createRange();
                  newRange?.setStart(textNode, 0);
                  newRange?.collapse(true);
                  sel?.addRange(newRange);
                  caretPostionRangeRef.current = newRange?.cloneRange();
                  found = true;
                  break;
                }
              }
            }
            if (!found) {
              if (div?.lastChild) {
                const lastTextNode = findLastTextNode(div);
                const newRange = document?.createRange();
                if (lastTextNode) {
                  newRange?.setStart(lastTextNode, lastTextNode?.textContent?.length || 0);
                } else {
                  newRange?.setStartAfter(div?.lastChild);
                }
                newRange?.collapse(true);
                sel?.addRange(newRange);
                caretPostionRangeRef.current = newRange?.cloneRange();
              }
            }
          }
        } else {
          if (div?.lastChild) {
            const lastTextNode = findLastTextNode(div);
            const newRange = document?.createRange();
            if (lastTextNode) {
              newRange?.setStart(lastTextNode, lastTextNode.textContent?.length || 0);
            } else {
              newRange?.setStartAfter(div?.lastChild);
            }
            newRange?.collapse(true);
            sel?.addRange(newRange);
            caretPostionRangeRef.current = newRange?.cloneRange();
          }
        }
      }
    } catch (error) {
      console.error('Error setting cursor position:', error);
    }
  };
  const findLastTextNode = (element) => {
    if (!element) return null;

    if (element?.nodeType === Node?.TEXT_NODE) {
      return element;
    }

    for (let i = element?.childNodes?.length - 1; i >= 0; i--) {
      const lastTextNode = findLastTextNode(element?.childNodes[i]);
      if (lastTextNode) {
        return lastTextNode;
      }
    }

    return null;
  };

  const insertAtCursor = (token) => {
    const div = divRef?.current;
    if (!div) return;

    div?.focus();

    const sel = window?.getSelection();
    if (!sel) return;

    let range;
    const span = document?.createElement('span');
    span.textContent = `${token?.label}`;
    span.style.color = token?.color || 'black';
    span.style.marginRight = '4px';
    if (caretPostionRangeRef?.current) {
      sel?.removeAllRanges();
      sel?.addRange(caretPostionRangeRef?.current);
      range = sel?.getRangeAt(0);
    } else {
      range = document?.createRange();
      if (div?.childNodes?.length > 0) {
        const lastChild = div?.lastChild;
        range?.setStartAfter(lastChild);
        range?.setEndAfter(lastChild);
      } else {
        range?.selectNodeContents(div);
        range?.collapse(false);
      }

      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    sel?.getRangeAt(0)?.insertNode(span);
    range = document.createRange();
    range?.setStartAfter(span);
    range?.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
    caretPostionRangeRef.current = range?.cloneRange();
    const text = div?.innerText;
    applyHighlightingAndPreserveCursor(text);
    setRawText(text);
  };

  const handleRuleNameChange = (e) => {
    setRuleName(e?.target?.value);
    if (validationErrors?.ruleName) {
      setValidationErrors((prev) => ({
        ...prev,
        ruleName: '',
      }));
    }
    if (validationSuccess) {
      setValidationSuccess(false);
    }
  };

  const handleInput = (e) => {
    const text = e?.currentTarget?.innerText;
    setRawText(text);
    if (validationErrors?.ruleExpression) {
      setValidationErrors((prev) => ({
        ...prev,
        ruleExpression: '',
      }));
    }
    if (validationSuccess) {
      setValidationSuccess(false);
    }
  };

  const parseTextToHtml = (text) => {
    let html = text?.replace(/:[a-zA-Z0-9_]+:/g, (match) => {
      const token = match?.slice(1, -1);
      const color = propertiesHash[token]?.color || 'black';
      return `<span style="color:${color}; margin-right: 4px;">${match}</span>`;
    });
    html = html?.replace(/\b(velocity|relative_velocity)\b/g, (match) => {
      return `<span style="background-color: #eae8f0; color: #7e62d1; padding: 3px 6px; border-radius: 8px;">${match}</span>`;
    });
    return html;
  };

  const handleBlur = () => {
    const div = divRef?.current;
    if (!div || document?.activeElement !== div) return;

    const text = div?.innerText;
    applyHighlightingAndPreserveCursor(text);
  };

  useEffect(() => {
    setInitialCaretPositionRange();
  }, [divRef?.current]);

  useEffect(() => {
    const div = divRef?.current;
    if (!div || document?.activeElement !== div) return;

    const text = div?.innerText;
    applyHighlightingAndPreserveCursor(text);
  }, [rawText]);

  const resetAllStates = () => {
    setRuleName('');
    setRawText('');
    setSelectedKey('1');
    setShowDescription(false);
    setActiveCategory(null);
    setSelectedItem(null);
    setVelocityType('transaction');
    setIsEnabled(true);
    setValidationErrors({
      ruleName: '',
      ruleExpression: '',
    });
    setValidationSuccess(false);

    if (divRef?.current) {
      divRef.current.innerHTML = '';
    }
    caretPostionRangeRef.current = 0;

    setIsEdit(false);
  };

  const handleCancelClick = () => {
    setShowConfirmModal(true);
  };
  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetAllStates();
    onClose();
  };

  const createRulePayload = async () => {
    if (!validateRule()) {
      return;
    }
    setIsRuleAdding(true);
    const payload = {
      name: ruleName,
      rule: rawText?.replace(/\n/g, '')?.trim(),
      enabled: isEnabled,
    };
    const response = isEdit ? await updateRule(store?._id, ruleData?._id, payload) : await createRule(store?._id, payload);
    if (response) {
      resetAllStates();
      onClose();
      refetch();
    }
    setIsRuleAdding(false);
  };

  const validateRule = () => {
    const { isValid, newValidationErrors } = ruleValidation(ruleName, rawText, getAllValidProperties, comparisonAllowedProps, stringOnlyFields);
    setValidationErrors(newValidationErrors);
    if (isValid) {
      setValidationSuccess(true);
    }
    return isValid;
  };

  const menuItems = [
    {
      key: '1',
      icon: <PredefinedIcon />,
      label: 'Predefined',
    },
    { key: '2', icon: <Attributes />, label: 'Attributes' },
    {
      key: '3',
      icon: <OperatorsIcon />,
      label: 'Operators',
    },
    { key: '4', icon: <VelocityIcon />, label: 'Velocities' },
  ];

  const getItems = () => {
    if (selectedKey === '1') return Library;
    if (selectedKey === '2') return properties;
    if (selectedKey === '3') return operators;
    return {};
  };

  const handleMenuClick = (e) => {
    setSelectedKey(e?.key);
    const selectedItem = menuItems?.find((item) => item?.key === e?.key);
    setActiveCategory(selectedItem);
    setSelectedItem(null);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    insertAtCursor(item);
    if (validationSuccess) {
      setValidationSuccess(false);
    }
  };

  const onAddSubmit = async (values) => {
    if (selectedKey !== '4') return;
    const { count_attribute, shared_attribute, time, frequency, transaction_status } = values;
    let tokenLabel = '';
    const countAttr = count_attribute?.value;
    const sharedAttr = shared_attribute?.value;
    const timeVal = `${time}${frequency?.value || 'm'}`;
    const txStatus = transaction_status?.value;
    if (velocityType === 'transaction' && countAttr && timeVal && txStatus) {
      tokenLabel = ` velocity(${countAttr}, ${timeVal}, ${txStatus})`;
    }

    if (velocityType === 'relative' && countAttr && sharedAttr && timeVal && txStatus) {
      tokenLabel = ` relative_velocity(${countAttr}_per_${sharedAttr}, ${timeVal}, ${txStatus})`;
    }

    if (tokenLabel) {
      insertAtCursor({ label: tokenLabel });
    }
  };

  const getAllValidProperties = () => {
    const properties = [];
    const allItems = getItems();
    Object.keys(allItems)?.forEach((heading) => {
      const categoryItems = allItems[heading];
      if (Array.isArray(categoryItems)) {
        categoryItems?.forEach((item) => {
          if (item && item?.label) {
            properties?.push(item?.label);
          }
          /*  if (item && item?.newlabel) {
            properties?.push(item?.newlabel);
          } */
        });
      }
    });

    commonProperties?.forEach((prop) => {
      if (!properties?.includes(prop)) {
        properties?.push(prop);
      }
    });
    return properties;
  };

  useEffect(() => {
    if (!isOpen) {
      resetAllStates();
    } else {
      const defaultCategory = menuItems?.find((item) => item?.key === '1');
      setActiveCategory(defaultCategory);
    }
  }, [isOpen]);

  return (
    <>
      <Modal className='create_rule' title={isEdit ? 'Update Rule' : 'Create Rule'} centered width={'90%'} destroyOnClose open={isOpen} onCancel={handleCancelClick} footer={null}>
        <Layout className='mid_section' style={{ background: '#fff', height: '70vh', display: 'flex' }}>
          <Sider width={100} style={{ background: '#f0f2f5' }}>
            <Menu mode='vertical' selectedKeys={[selectedKey]} onClick={(e) => handleMenuClick(e)} items={menuItems} style={{ color: 'black' }} />
          </Sider>
          <Layout style={{ display: 'flex', flexDirection: 'row', padding: '0' }}>
            <Content
              className='properties'
              style={{
                maxWidth: '310px',
                minWidth: '310px',
                overflowY: 'auto',
                borderRight: '1px solid #ddd',
              }}>
              {selectedKey === '4' ? (
                <div style={{ padding: '24px', paddingTop: '16px' }}>
                  <Title level={5} style={{ padding: '0', marginBottom: '8px' }}>
                    Velocity types
                  </Title>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderBottom: '1px solid #dce0e5',
                      paddingBottom: '32px',
                    }}>
                    <label
                      style={{
                        display: 'flex',
                        gap: '4px',
                        marginBottom: '8px',
                        alignItems: 'center',
                        fontWeight: 400,
                      }}>
                      <input type='radio' name='velocity' checked={velocityType === 'transaction'} onChange={() => setVelocityType('transaction')} /> Transactional Velocity
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        fontWeight: 400,
                      }}>
                      <input type='radio' name='velocity' checked={velocityType === 'relative'} onChange={() => setVelocityType('relative')} /> Relative Velocity
                    </label>
                  </div>
                  <Form layout='vertical' onFinish={onAddSubmit} style={{ marginTop: '24px' }}>
                    <Form.Item
                      name='count_attribute'
                      label='Attribute to count'
                      className='customInput mobileInputView'
                      rules={[
                        {
                          required: true,
                          message: 'Attribute is required',
                        },
                      ]}>
                      <Select defaultValue={'Select an attribute'} optionsValue={velocityType === 'relative' ? relative_attributes : transaction_attributes} />
                    </Form.Item>
                    {velocityType === 'relative' && (
                      <Form.Item
                        name='shared_attribute'
                        label='Shared attribute'
                        className='customInput mobileInputView'
                        rules={[
                          {
                            required: true,
                            message: 'Attribute is required',
                          },
                        ]}>
                        <Select defaultValue={'Select an attribute'} optionsValue={relative_attributes} />
                      </Form.Item>
                    )}
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <Form.Item
                        name='time'
                        label='Time'
                        className='mobileInputView'
                        style={{ width: '100%' }}
                        rules={[
                          { required: true, message: 'Time is required' },
                          {
                            message: 'Only positive numbers allowed',
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              if (/^\d+$/.test(value) && Number(value) > 0) {
                                return Promise.resolve();
                              } else {
                                return Promise.reject('Invalid Time');
                              }
                            },
                          },
                        ]}>
                        <Input type='number' placeholder='Enter Time' name='time' style={{ height: '36px' }} />
                      </Form.Item>
                      <Form.Item name='frequency' label='Frequency' className='customInput mobileInputView' style={{ width: '100%' }}>
                        <Select optionsValue={frequency} />
                      </Form.Item>
                    </div>
                    <Form.Item
                      name='transaction_status'
                      label='Transactions Status'
                      style={{
                        borderBottom: '1px solid #dce0e5',
                        paddingBottom: '32px',
                      }}
                      className='customInput mobileInputView'
                      rules={[
                        {
                          required: true,
                          message: 'Transaction status is required',
                        },
                      ]}>
                      <Select defaultValue={'Select transaction status'} optionsValue={options} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        style={{
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginTop: '16px',
                        }}
                        block
                        className='secondary-btn rounded-lg'
                        htmlType='submit'>
                        <PlusOutlined style={{ fontSize: '16px' }} /> Add velocity
                      </Button>
                    </div>
                  </Form>
                </div>
              ) : (
                Object.keys(getItems())?.map((heading) => (
                  <div key={heading}>
                    <Title
                      level={5}
                      style={{
                        borderBottom: '1px solid #dce0e5',
                        background: '#f9fafa',
                        padding: '7px 16px',
                        paddingRight: '0',
                      }}>
                      {heading}
                    </Title>
                    {getItems()[heading]?.map((item) => (
                      <>
                        <p
                          key={item?.label}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            cursor: 'pointer',
                            alignItems: 'center',
                            padding: '16px',
                            marginBottom: '0',
                            borderBottom: '1px solid #dce0e5',
                            color: item?.color,
                          }}
                          onClick={() => handleItemClick(item)}>
                          {selectedKey === '1' ? (
                            <>
                              <span
                                style={{
                                  display: 'inline-block',
                                  border: selectedItem?.label === item?.label && '1px solid #7e62d1',
                                  padding: selectedItem?.label === item?.label && '2px 8px',
                                  borderRadius: '4px',
                                }}>
                                {item?.heading}
                              </span>
                            </>
                          ) : (
                            <>
                              {/* <p style={{ fontWeight: '700' }}>{item?.heading}</p> */}
                              <span
                                style={{
                                  display: 'inline-block',
                                  wordBreak: 'break-all',
                                  border: selectedItem?.label === item?.label ? '1px solid #7e62d1' : 'none',
                                  padding: selectedItem?.label === item?.label ? '2px' : '0',
                                  paddingRight: selectedItem?.label === item?.label ? '8px' : '0',
                                  borderRadius: '4px',
                                }}>
                                {item?.label}
                              </span>
                            </>
                          )}
                          <span style={{ color: '#7e62d1', marginBottom: '4px' }}>
                            <PlusOutlined />
                          </span>
                        </p>
                      </>
                    ))}
                  </div>
                ))
              )}
            </Content>

            <Content
              className='right-portion'
              style={{
                maxWidth: '61.5vw',
                width: '100%',
                display: 'flex',
                padding: '16px',
                gap: '24px',
                overflow: 'auto',
                marginBottom: '24px',
              }}>
              <div style={{ flex: showDescription ? 1.3 : 1.8 }}>
                <Title style={{ paddingTop: '16px' }} level={5}>
                  Rule Name
                </Title>
                <Input
                  placeholder='Enter rule name'
                  value={ruleName}
                  onChange={handleRuleNameChange}
                  style={{
                    height: '40px',
                    marginBottom: '2px',
                    borderColor: validationErrors?.ruleName ? 'red' : undefined,
                  }}
                />
                {validationErrors?.ruleName && <p style={{ color: 'red', fontSize: '12px' }}>{validationErrors?.ruleName}</p>}

                <Title level={5} style={{ display: 'flex', alignItems: 'center', marginTop: '24px' }}>
                  Rule Expression
                </Title>
                <div
                  ref={divRef}
                  onClick={() => {
                    caretPostion();
                  }}
                  contentEditable
                  suppressContentEditableWarning
                  className='editableDiv'
                  onInput={handleInput}
                  onBlur={handleBlur}
                  tabIndex={0}
                  style={{
                    border: validationErrors?.ruleExpression ? '1px solid red' : undefined,
                  }}
                />
              </div>
              <div
                style={{
                  flex: showDescription ? 1 : 0.2,
                  minWidth: showDescription && '300px',
                }}>
                {window?.innerWidth > 1240 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button className='secondary-btn' onClick={() => setShowDescription(!showDescription)}>
                      {showDescription ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <EyeInvisibleOutlined /> Hide Description
                        </span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <EyeOutlined /> Show Description
                        </span>
                      )}
                    </Button>
                  </div>
                )}

                {showDescription && (
                  <>
                    <Title level={5} style={{ marginTop: '8px', marginBottom: '32px' }}>
                      Description
                    </Title>
                    {activeCategory && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '24px',
                        }}>
                        {activeCategory?.icon}
                        <Title level={5} style={{ margin: 0 }}>
                          {selectedKey !== '4' ? activeCategory?.label : velocityType === 'relative' ? 'Relative Velocity' : 'Transactional Velocity'}
                        </Title>
                      </div>
                    )}
                    {selectedKey !== '4' ? (
                      selectedItem ? (
                        <div>
                          <Title
                            level={5}
                            style={{
                              marginBottom: '16px',
                              color: selectedItem?.color,
                            }}>
                            {selectedKey === '1' ? selectedItem?.heading : selectedItem?.label}
                          </Title>
                          <Paragraph style={{ color: '#555' }}>{selectedItem?.description}</Paragraph>
                        </div>
                      ) : (
                        <>
                          <Paragraph style={{ color: '#555' }}>These are some common rules that many businesses have found useful to include in their routing.</Paragraph>
                          <Paragraph style={{ color: '#555' }}>Clicking on one will insert the whole combination, and you can then update anything you wish to personalise for your needs.</Paragraph>
                        </>
                      )
                    ) : velocityType === 'transaction' ? (
                      'Velocity element to identify high frequency transactions. Will return the frequency of a particular attribute in the transactions over a time period. velocity(billing_address,24h,approved) > 10  This will trigger when the number of approved requests (velocity) for a particular billing address goes above 10 in a 24 hour period.'
                    ) : (
                      'Relative Velocity element to identify cases when the same attribute is reused in other unrelated transactions. Returns the occurrence of one attribute in relation to another over a time period. velocity(cardholder_per_email,30d,attempted) > 10 This will trigger when a single cardholder uses more than 10 email addresses in any 30 day period across their attempted transactions.'
                    )}
                  </>
                )}
              </div>
            </Content>
          </Layout>
        </Layout>
        <div className='rule_footer' style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_DEVELOPER]) && (
              <Button className='primary-btn primaryBtn' onClick={createRulePayload} loading={isRuleAdding}>
                {isEdit ? 'Update Rule' : 'Create Rule'}
              </Button>
            )}
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_DEVELOPER]) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button className='secondary-btn' onClick={validateRule}>
                  Check Rule
                </Button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Switch checked={isEnabled} defaultChecked={isEnabled} onChange={setIsEnabled} />
                  <span style={{ minWidth: 60, color: isEnabled ? 'green' : 'red', fontWeight: 600 }}>{isEnabled ? 'Active' : 'Inactive'}</span>
                </div>
                {validationSuccess && <span style={{ color: 'green', fontSize: '14px' }}>✓ Rule syntax is consistent</span>}
                {validationErrors?.ruleExpression && <span style={{ color: 'red', fontSize: '14px' }}>{validationErrors?.ruleExpression}</span>}
              </div>
            )}
          </div>
          <Button className='secondary-btn' onClick={handleCancelClick}>
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal open={showConfirmModal} centered onCancel={() => setShowConfirmModal(false)} onOk={handleConfirmClose} zIndex={2000} footer={false}>
        <header className='font-semibold text-base mb-4'>You're leaving the rule builder</header>
        <div className='mb-8'>Would you like to Return and save your rule, or click Exit rule builder to discard your changes ?</div>
        <div className='flex mt-4 popupConfirmBtn' style={{ justifyContent: 'end' }}>
          <Button className='primary-btn primaryBtn return-button' key='cancel' onClick={() => setShowConfirmModal(false)}>
            Return and Save
          </Button>
          <Button className='DeleteBtn ml-4 rounded-lg' key='confirm' type='primary' danger onClick={handleConfirmClose}>
            Exit rule builder
          </Button>
        </div>
      </Modal>
    </>
  );
}
